import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getMemberInfo } from "@/hooks/useMembers";
import MInputField from "@/components/ui/atoms/MInputField";
import { MButton } from "@/components/ui/atoms/MButtons";
import { useReminders } from "@/hooks/useReminders";
import { ReminderType, UpdateReminder } from "@/types/reminders";
import {
  displayEventForField,
  paintEventDateField,
  paintHeader,
  paintReminderDateField,
  paintReminderNotesField,
  paintReminderTimeField,
} from "./remindersHelper";
import { initDate, inputFieldTime, updateTime } from "@/utils/date-utils";
import MFrame from "@/components/ui/atoms/MFrame";

/**
 * Insurance Reminder Creation Page
 * Allows users to select an insurance type to create a reminder for
 */
const EditReminderFormPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const reminder = location.state?.reminder;
  const type = reminder?.type.toLowerCase();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const { updateReminder } = useReminders(memberId);
  const [eventDate, setEventDate] = useState<Date | null>(
    initDate(reminder?.eventDate)
  );
  const [eventTime, setEventTime] = useState<string | null>(
    inputFieldTime(reminder?.eventDate)
  );
  const [policyNumber, setPolicyNumber] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(
    initDate(reminder?.startDate)
  );
  const [endDate, setEndDate] = useState<Date | null>(
    initDate(reminder?.endDate)
  );
  const [reminderTime, setReminderTime] = useState<string | null>(
    inputFieldTime(reminder?.startDate)
  );
  const [eventFor, setEventFor] = useState<string | null>(
    reminder?.reminderFor
  );
  const [notes, setNotes] = useState<string | null>(reminder?.notes);
  const [isFormValid, setIsFormValid] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: boolean;
  }>({});
  const [validationErrorMsg, setValidationErrorMsg] = useState<{
    [key: string]: string;
  }>({});
  const selectedDays = reminder?.selectedDays.map((day) => day.toString());
  const [firstDosage, setFirstDosage] = useState<string | null>(null);
  const [secondDosage, setSecondDosage] = useState<string | null>(null);
  const [thirdDosage, setThirdDosage] = useState<string | null>(null);
  const [fourthDosage, setFourthDosage] = useState<string | null>(null);

  useEffect(() => {
    if (reminder != null) {
      setPolicyNumber(reminder.reminderAttrs?.policyNumber);
    }
  }, [reminder]);

  const validateCurrentStage = (): boolean => {
    const isValid = true;
    const newErrors: { [key: string]: boolean } = {};
    setValidationErrors(newErrors);
    return isValid;
  };

  const handleUpdateReminder = async () => {
    const reminderData: UpdateReminder = {
      startDate: updateTime(startDate, reminderTime),
      endDate: updateTime(endDate, reminderTime),
      reminderFor: eventFor,
      eventDate: eventDate,
      notes: notes,
      reminderAttrs: {
        policyNumber: policyNumber,
      },
    };
    await updateReminder(reminder.id, reminderData as UpdateReminder);
    navigate("/reminders");
  };

  const eventForField = () => {
    return displayEventForField(eventFor, t, type);
  };

  const eventDateField = () => {
    return paintEventDateField(
      eventDate,
      setEventDate,
      t,
      "insurance.expiryDate"
    );
  };

  const insuranceFields = () => {
    return (
      <MInputField
        label={t("reminders.insurance.policyNumberLabel")}
        placeholder={t("common.notAnswered")}
        value={policyNumber}
        onChange={(e) => setPolicyNumber(e.target.value)}
      />
    );
  };

  const reminderStartDateField = (labelKey?: string) => {
    return paintReminderDateField(
      startDate,
      setStartDate,
      eventDate,
      t,
      labelKey,
      true
    );
  };

  const reminderTimeField = () => {
    return paintReminderTimeField(reminderTime, setReminderTime, startDate, t);
  };

  const reminderNotesField = (textAreaPlaceHolderKey?: string) => {
    return paintReminderNotesField(notes, setNotes, t, textAreaPlaceHolderKey);
  };

  const reminderFields = (textAreaPlaceHolderKey?: string) => {
    return (
      <>
        {reminderStartDateField()}
        {reminderTimeField()}
        {reminderNotesField(textAreaPlaceHolderKey)}
      </>
    );
  };

  const paintFieldsByType = () => {
    switch (reminder?.type) {
      case ReminderType.INSURANCE:
        return (
          <>
            {eventForField()}
            {insuranceFields()}
            {eventDateField()}
            {reminderFields(type)}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="profile-page-root">
      {paintHeader(memberInfo, type)}
      <MFrame>
        <div className="flex flex-col gap-8 w-full">{paintFieldsByType()}</div>
      </MFrame>
      <div className="confirm-footer">
        <MButton
          variant="filled"
          size="md"
          className="w-full"
          onClick={() => handleUpdateReminder()}
        >
          {t("reminders.update.button")}
        </MButton>
      </div>
    </div>
  );
};

export default EditReminderFormPage;
